import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InputField from '../ui/InputField';
import SelectField from '../ui/SelectField';
import SearchableSelectField from '../ui/SearchableSelectField';
import {
    useGetOrganizationQuery,
    useGetOrganizationsQuery,
} from '../../api/organization/organizationApiSlice';
import { tenantSelector } from '../../selector/authenticationSelector';
import { isSelfServiceTenant } from '../../common/tenantStore';
import { COST_CENTER_MAX_LENGTH, defaultCostCenterFor } from '../../utils/costCenter';
import {
    OrganizationContactPrefill,
    PrefillAddressField,
    filledAddressFields,
    organizationContactPrefill,
    withClearedLeftovers,
} from '../../utils/organizationPrefill';

/** Who a booking is made for, and the invoice recipient's cost centre (#1810). */
export interface BookingAttribution {
    organizationId: string | null;
    organizationUnitId: string | null;
    /** Form value; blank means "none" (see `costCenterPayload`). */
    costCenter: string;
}

export const EMPTY_BOOKING_ATTRIBUTION: BookingAttribution = {
    organizationId: null,
    organizationUnitId: null,
    costCenter: '',
};

/** The list endpoint's ceiling (`MAX_PAGE_SIZE` in organizationController). */
const ORGANIZATION_PAGE_SIZE = 100;
const NONE = '__none__';

interface Props {
    value: BookingAttribution;
    onChange: (next: BookingAttribution) => void;
    disabled?: boolean;
    /**
     * A booking an organisation covers keeps that organisation — it is the one
     * paying, and the backend refuses to re-attribute it. The cost centre
     * stays editable.
     */
    organizationLocked?: boolean;
    /**
     * The booking's stored attribution could not be read. The fields are
     * replaced by a message — an empty field would read as "none" — and the
     * edit sends no attribution (`attributionPayloadForEdit`).
     */
    loadFailed?: boolean;
    /**
     * Called when the operator PICKS an organisation, with its legal name and
     * billing address for the booking's customer fields (ADR-0012 amendment
     * 2026-10-12: the booking's company and address are the invoice
     * recipient). Not called for "none", nor when the form seeds itself from a
     * stored booking. Forms without customer fields leave it out.
     */
    onOrganizationPicked?: (prefill: OrganizationContactPrefill) => void;
}

/**
 * Organisation, unit and cost centre on a booking form (#1810, ADR-0012
 * amendment 2026-10-04). One component for every booking form — room, venue,
 * non-room, series; create and edit — so they cannot answer differently.
 *
 * - **Picking an organisation or unit prefills the cost centre** with the
 *   unit's default, else the organisation's, until the operator types in the
 *   field themselves. The backend stores exactly what the field shows.
 * - **Re-picking replaces the prefilled value**: the previous organisation's
 *   cost centre must not ride along onto another customer's invoice.
 * - **Operational tenants only.** Organisations and the cost-centre columns
 *   exist in the operational store; a legacy tenant gets no field, rather than
 *   one whose value the backend would refuse.
 */
const BookingAttributionFields: React.FC<Props> = ({
    value,
    onChange,
    disabled = false,
    organizationLocked = false,
    loadFailed = false,
    onOrganizationPicked,
}) => {
    const { t } = useTranslation();
    const tenant = useSelector(tenantSelector);
    const operational = isSelfServiceTenant(tenant);

    const { data: list, isLoading: loadingList, isError: listFailed } = useGetOrganizationsQuery(
        { pageSize: ORGANIZATION_PAGE_SIZE },
        { skip: !operational },
    );
    const { data: detail, isFetching: loadingDetail } = useGetOrganizationQuery(
        { id: value.organizationId ?? '' },
        { skip: !operational || !value.organizationId },
    );

    // Typed by the operator in this form session: a later re-pick must not
    // overwrite it. Prefilled values are the defaults and are replaced.
    const [costCenterTyped, setCostCenterTyped] = React.useState(false);

    // An organisation belongs to one tenant: a switch clears the attribution
    // instead of carrying another tenant's organisation into this booking.
    // Address fields the last organisation pick filled — cleared again when a
    // different organisation without them is picked (`withClearedLeftovers`).
    const prefilledFields = React.useRef<Set<PrefillAddressField>>(new Set());

    const previousTenant = React.useRef(tenant);
    React.useEffect(() => {
        if (previousTenant.current === tenant) return;
        previousTenant.current = tenant;
        setCostCenterTyped(false);
        prefilledFields.current = new Set();
        onChange(EMPTY_BOOKING_ATTRIBUTION);
    }, [tenant, onChange]);

    const organizations = list?.items ?? [];
    const truncated = (list?.total ?? 0) > organizations.length;
    const activeUnits = (detail?.units ?? []).filter((u) => u.archivedAt === null);

    const organizationOptions = React.useMemo(
        () => [
            { value: NONE, label: t('create_booking.attribution.organization_none') },
            ...organizations.map((o) => ({
                value: o.id,
                label: o.displayName?.trim() || o.legalName,
                ...(o.displayName?.trim() ? { description: o.legalName } : {}),
            })),
            // The selected organisation stays nameable when the list does not
            // carry it (archived since, or past the first page).
            ...(value.organizationId && detail && !organizations.some((o) => o.id === value.organizationId)
                ? [{ value: detail.id, label: detail.displayName?.trim() || detail.legalName }]
                : []),
        ],
        [organizations, detail, value.organizationId, t],
    );

    const unitOptions = React.useMemo(() => [NONE, ...activeUnits.map((u) => u.id)], [activeUnits]);
    const unitLabels = React.useMemo(() => {
        const labels: Record<string, string> = { [NONE]: t('create_booking.attribution.unit_none') };
        activeUnits.forEach((u) => {
            labels[u.id] = u.name;
        });
        return labels;
    }, [activeUnits, t]);

    const orgDefaultOf = (organizationId: string | null): string | null =>
        organizations.find((o) => o.id === organizationId)?.defaultCostCenter
        ?? (detail?.id === organizationId ? detail.defaultCostCenter : null)
        ?? null;

    const pickOrganization = (picked: string) => {
        const organizationId = picked === NONE ? null : picked;
        const pickedOrganization = organizations.find((o) => o.id === organizationId)
            ?? (detail?.id === organizationId ? detail : undefined);
        if (pickedOrganization && organizationId !== value.organizationId) {
            const prefill = organizationContactPrefill(pickedOrganization);
            onOrganizationPicked?.(withClearedLeftovers(prefill, prefilledFields.current));
            prefilledFields.current = filledAddressFields(prefill);
        }
        onChange({
            organizationId,
            organizationUnitId: null,
            costCenter: costCenterTyped
                ? value.costCenter
                : defaultCostCenterFor(null, orgDefaultOf(organizationId)),
        });
    };

    const pickUnit = (picked: string) => {
        const organizationUnitId = picked === NONE ? null : picked;
        const unit = activeUnits.find((u) => u.id === organizationUnitId);
        onChange({
            ...value,
            organizationUnitId,
            costCenter: costCenterTyped
                ? value.costCenter
                : defaultCostCenterFor(unit?.defaultCostCenter, orgDefaultOf(value.organizationId)),
        });
    };

    if (!operational) return null;
    if (loadFailed) {
        return (
            <p className={'text-sm text-foreground/70'}>
                {t('create_booking.attribution.load_failed')}
            </p>
        );
    }

    return (
        <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
            <div className={'flex flex-col gap-1'}>
                <label className={'text-sm font-medium text-foreground/80'}>
                    {t('create_booking.attribution.organization')}
                </label>
                <SearchableSelectField
                    value={value.organizationId ?? NONE}
                    onChange={pickOrganization}
                    options={organizationOptions}
                    placeholder={
                        loadingList
                            ? t('create_booking.attribution.organization_loading')
                            : t('create_booking.attribution.organization_placeholder')
                    }
                    searchPlaceholder={t('create_booking.attribution.organization_search')}
                    emptyMessage={t('create_booking.attribution.organization_empty')}
                    disabled={disabled || organizationLocked || loadingList}
                />
                {listFailed && (
                    <p className={'text-xs text-foreground/70'}>
                        {t('create_booking.attribution.organization_load_failed')}
                    </p>
                )}
                {truncated && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('create_booking.attribution.organization_truncated', {
                            count: organizations.length,
                        })}
                    </p>
                )}
                {organizationLocked && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('create_booking.attribution.organization_locked')}
                    </p>
                )}
            </div>
            {value.organizationId && (
                <SelectField
                    labelAbove={true}
                    label={t('create_booking.attribution.unit')}
                    value={value.organizationUnitId ?? NONE}
                    onChange={pickUnit}
                    options={unitOptions}
                    optionLabels={unitLabels}
                    loading={loadingDetail}
                    disabled={disabled || organizationLocked}
                    noOptions={t('create_booking.attribution.unit_none')}
                />
            )}
            <div className={'sm:col-span-2'}>
                <InputField
                    labelAbove={true}
                    label={t('create_booking.attribution.cost_center')}
                    help={t('create_booking.attribution.cost_center_help')}
                    placeholder={t('create_booking.attribution.cost_center_placeholder')}
                    value={value.costCenter}
                    onChange={(val) => {
                        setCostCenterTyped(true);
                        onChange({ ...value, costCenter: val });
                    }}
                    maxLength={COST_CENTER_MAX_LENGTH}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

export default BookingAttributionFields;
